<template>
  <div v-if="quoteDistribution.id">
    <div v-if="isOwnQuoteDistribution()">
      <b-card
        no-body
      >
        <div class="m-2">
          <div class="row">
            <div class="col-md-6">
              <table class="table table-bordered">
                <tr>
                  <th>{{ $t('From Org') }}</th>
                  <td> {{ $langName(quoteDistribution.from_org.name) }}</td>
                </tr>
                <tr>
                  <th>{{ $t('Period') }}</th>
                  <td> {{ quoteDistribution.period.admission_year + '-' + quoteDistribution.period.name }}</td>
                </tr>
              </table>
            </div>
            <div class="col-md-6">
              <table class="table table-bordered">
                <tr>
                  <th>{{ $t('Description') }}</th>
                  <td> {{ quoteDistribution.about }}</td>
                </tr>
                <tr>
                  <th>{{ $t('File') }}</th>
                  <td>
                    <router-link
                      v-if="quoteDistribution.file"
                      :to="{ name: 'pdf-viewer', params: { url: quoteDistribution.file }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        class="cursor-pointer"
                        size="16"
                      />
                    </router-link>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </b-card>
      <InnovationBalance />
      <b-card no-body>
        <div class="m-1">

          <!-- Table Top -->
          <b-row>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('Search')"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-table
          :tbody-tr-class="rowClass"
          :fields="fieldsAccepted"
          :items="otmItmAcceptedLetterQuotes"
          :filter="searchQuery"
          class="table-bordered"
        >
          <template #head()="data">
            {{ getHead(data) }}
          </template>
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>
          <template #cell(name)="data">
            {{ $langName(data.value) }}
          </template>

          <template
            v-for="dcType in dcTypes"
            v-slot:[`cell(${dcType.id})`]="data"
          >
            {{ getCellValueByDcType(data, dcType) }}
          </template>

          <template #cell(actions)="data">

            <feather-icon
              v-if="canUpdate(quoteDistribution) && data.item.type !=='ministry'"
              v-b-toggle.sidebar-update-quote-distribution-item
              icon="EditIcon"
              class="text-primary cursor-pointer mr-1"
              size="16"
              :title="$t('Update')"
              @click="showUpdate(data)"
            />
          </template>
        </b-table>
        <update-item @refetchData="loadBalance(); loadAcceptedLetterQuotes()" />
        <b-button
          v-if="canSend(quoteDistribution)"
          v-b-toggle.sidebar-send-quote-distribution
          variant="primary"
          class="btn btn-primary ml-auto m-2"
        >
          <feather-icon
            icon="SendIcon"
            class="mr-50"
          />
          <span>{{ $t('Send') }}</span>
        </b-button>
      </b-card>
      <send @refetchData="refetchData" />
    </div>
    <div v-else>
      <quote-distribution-component :quote-distribution="quoteDistribution" />
    </div>
  </div>
  <div v-else>
    <div class="d-flex justify-content-center mb-5">
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>
  </div>
</template>

<script>
import {
  BCard, BTable, VBToggle, BCol, BSpinner,
  BRow, BFormInput, BButton, VBModal,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import quoteDistributionModule from '@/store/quote-distribution'
import permission from './permission'
import UpdateItem from './UpdateItem.vue'
import Send from './Send.vue'
import { myOrganization } from '@/auth/utils'
import quoteDistributionComponent from '@/views/components/QuoteDistributionComponent.vue'
import InnovationBalance from '@/views/components/InnovationBalance.vue'

export default {
  components: {
    InnovationBalance,
    BCard,
    BTable,
    UpdateItem,
    BCol,
    BRow,
    BFormInput,
    BButton,
    Send,
    quoteDistributionComponent,
    BSpinner,
  },
  directives: {
    'b-toggle': VBToggle,
    'b-modal': VBModal,
  },
  computed: {
    quoteDistribution() {
      return this.$store.state[this.MODULE_NAME].quoteDistribution
    },
    otmItmAcceptedLetterQuotes() {
      return this.$store.state[this.MODULE_NAME].otmItmAcceptedLetterQuotes
    },
    balance() {
      return this.$store.state[this.MODULE_NAME].balance
    },
    dcTypes() {
      return this.$store.getters['staticModule/getSimpleDcTypes']
    },
  },
  created() {
    this.refetchData()
    const { dcTypes } = this
    dcTypes.forEach(dcType => {
      this.fieldsAccepted.push({
        key: `${dcType.id}`,
        thClass: 'h6',
      })
    })
    this.fieldsAccepted.push({
      key: 'actions',
      label: '',
    })
  },
  methods: {
    rowClass(item, type) {
      if (item && type === 'row') {
        if (item.type === 'ministry') {
          return 'text-bold font-weight-bold bg-light'
        }
        return 'text-secondary'
      }
      return null
    },
    isOwnQuoteDistribution() {
      return myOrganization() && this.quoteDistribution.from_org.id === myOrganization().id
    },
    showUpdate(data) {
      this.$store.commit(`${this.MODULE_NAME}/SET_QUOTE_DISTRIBUTION_ITEM`, data.item)
    },
    refetchData() {
      this.$store.commit(`${this.MODULE_NAME}/FRESH`)
      this.$store.dispatch(`${this.MODULE_NAME}/get`, this.$route.params.id).then(() => {
        if (this.isOwnQuoteDistribution()) {
          this.loadAcceptedLetterQuotes()
        }
      })
    },
    loadAcceptedLetterQuotes() {
      this.$store.dispatch(`${this.MODULE_NAME}/getOtmItmAcceptedLetterQuotes`, { total_quote: this.quoteDistribution.id })
    },
    getDcType(id) {
      return this.$store.getters['staticModule/getDcType'](id)
    },
    getHead(head) {
      const col = head.label
      if (Number(col) >= 100 && Number(col) <= 700) {
        return this.$langName(this.getDcType(Number(col)).name)
      }
      return this.$t(col)
    },
    getQuoteOrgType(id) {
      return this.$store.getters['staticModule/getQuoteOrgType'](id)
    },
    getValueOfItemInBalance(data) {
      let res = 0
      this.balance.forEach(orgTypes => {
        if (orgTypes.org_type === data.org_type) {
          orgTypes.sum.forEach(item => {
            if (item.dc_type === data.dc_type) {
              res += item.sum
            }
          })
        }
      })
      return res
    },
    getCellValueByDcType(data, dcType) {
      const have = Number(data.item[`item_${dcType.id}`])
      const total = data.value
      return `${have || 0}/${total || 0}`
    },
  },
  setup() {
    const {
      DSc, PhD, traineeResearcher,
      quoteOrgTypeOTM, quoteOrgTypeITM, quoteOrgTypeCommon,
      totalQuoteTypeRequest,
    } = store.state.staticModule
    const fieldsAccepted = [
      { key: 'index', label: '#' },
      { key: 'name', label: 'Organization' },
    ]
    const fields = [
      { key: 'dc_itm', label: `${DSc}`, data: { org_type: quoteOrgTypeITM, dc_type: DSc } },
      { key: 'phd_itm', label: `${PhD}`, data: { org_type: quoteOrgTypeITM, dc_type: PhD } },
      { key: 'dc_otm', label: `${DSc}`, data: { org_type: quoteOrgTypeOTM, dc_type: DSc } },
      { key: 'phd_otm', label: `${PhD}`, data: { org_type: quoteOrgTypeOTM, dc_type: PhD } },
      { key: 'trainee_researcher', label: `${traineeResearcher}`, data: { org_type: quoteOrgTypeCommon, dc_type: traineeResearcher } },
    ]
    const searchQuery = ''
    const MODULE_NAME = 'quoteDistribution'
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, quoteDistributionModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })
    return {
      MODULE_NAME,
      DSc,
      PhD,
      traineeResearcher,
      fieldsAccepted,
      fields,
      quoteOrgTypeITM,
      quoteOrgTypeOTM,
      quoteOrgTypeCommon,
      totalQuoteTypeRequest,
      ...permission(),
      searchQuery,
    }
  },
}
</script>

<style>

</style>
